//import React, { useEffect } from 'react';
import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

// Import Material-UI icons
import ListAltIcon from '@mui/icons-material/ListAlt';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FavoriteIcon from '@mui/icons-material/Favorite';

export default function MainHome() {
  const [value, setValue] = React.useState<string | null>(null);
  const [options, setOptions] = React.useState<string[]>([]);
  const [inputValue, setInputValue] = React.useState('');

  // Debounce function
  const debounce = (func: Function, wait: number) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Cached results
  const searchCache = React.useRef<Record<string, string[]>>({});

  const runSearch = (query: string) => {
    const q = query.trim();
    if (!q) return;
    window.location.href = `/search?q=${encodeURIComponent(q)}`;
  };

  // Debounced autocomplete function
  const fetchAutoComplete = React.useCallback(
    debounce(async (searchTerm: string) => {
      // Don't search for less than 2 characters
      if (searchTerm.length < 2) {
        setOptions([]);
        return;
      }

      // Check cache first
      if (searchCache.current[searchTerm]) {
        setOptions(searchCache.current[searchTerm]);
        return;
      }

      try {
        const response = await fetch(`/search/autocomplete?q=${encodeURIComponent(searchTerm)}`);
        const data = await response.json();
        const completions: string[] = Array.isArray(data?.completions) ? data.completions : [];
        // Cache the results
        searchCache.current[searchTerm] = completions;
        setOptions(completions);
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        setOptions([]);
      }
    }, 300), // 300ms delay
    []
  );

  const optionsList = [
    { name: 'AI Chat', link: '/chat', icon: ChatIcon, color: '#FF69B4' },
    { name: 'Leaderboard', link: '/leaderboard', icon: ListAltIcon, color: '#4CAF50' },
    { name: 'Projects', link: '/my_projects', icon: FolderOpenIcon, color: '#9C27B0' },
    { name: 'Favorites', link: '/favorites', icon: FavoriteIcon, color: '#E91E63' },
    { name: 'Analyze Earnings Calls', link: '/earnings_calls', icon: RecordVoiceOverIcon, color: '#FF9800' },
    { name: 'Analyze SEC Filings', link: '/sec_filings', icon: DescriptionIcon, color: '#795548' },
    { name: 'Pricing', link: '/pricing', icon: CreditCardIcon, color: '#607D8B' },
    { name: 'Insights', link: '/insights', icon: LightbulbIcon, color: '#2196F3' },
    { name: 'Stock screener', link: '/screener', icon: SearchIcon, color: '#00BCD4' },
  ];

  return (
    <>
      <style>
        {`.home-container {
            max-width: 800px;
            margin: 0 auto;
            padding: 2rem;
          }
          .search-section {
            text-align: center;
            margin-bottom: 2rem;
          }
          .search-section h1 {
            white-space: nowrap;
            font-size: clamp(1.5rem, 4vw, 2rem); /* Responsive font size */
          }
          .search-form {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }
          .search-button {
            background-color: #007bff;
            color: white;
            padding: 10px;
          }
          .search-button:hover {
            background-color: #0056b3;
          }
          .options-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }
          .option-card {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem;
            background-color: #f8f9fa;
            border-radius: 4px;
            text-decoration: none;
            color: #6c757d; /* Lighter font color */
            transition: background-color 0.3s, color 0.3s;
          }
          .option-card:hover {
            background-color: #e9ecef;
            color: #495057; /* Slightly darker on hover for better contrast */
            text-decoration: none;
          }
          .new-page-icon {
            font-size: 1.2rem;
          }
          .option-icon {
            margin-right: 8px;
          }
          .option-name {
            font-weight: 500; /* Medium font weight for better readability */
          }
          .announcement-container {
            width: 100%;
            display: flex;
            justify-content: center;
            margin-bottom: 2rem;
            min-width: 0;
          }

          .announcement-banner {
            background-color: rgb(219, 242, 241);
            border-radius: 100px;
            padding: 8px 20px;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: transform 0.2s;
            font-size: clamp(0.8rem, 3vw, 1rem);
            max-width: 100%;
            box-sizing: border-box;
          }

          .announcement-banner:hover {
            transform: translateY(-2px);
          }

          .announcement-tag {
            background-color: rgb(75, 135, 139);
            color: white;
            padding: 4px 12px;
            border-radius: 100px;
            font-size: clamp(0.75rem, 2.5vw, 0.875rem);
            font-weight: 500;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .announcement-text {
            color: rgb(51, 51, 51);
            font-size: clamp(0.8rem, 3vw, 0.9375rem);
            font-weight: 500;
            line-height: 1.4;
            min-width: 0;
            overflow-wrap: anywhere;
          }

          .announcement-link {
            text-decoration: none;
            display: inline-block;
            max-width: 100%;
          }

          .announcement-arrow {
            display: inline-block;
            font-size: 18px;
            margin-left: 4px;
          }

          @media (max-width: 600px) {
            .announcement-container {
              margin-bottom: 1.5rem;
            }

            .announcement-link,
            .announcement-banner {
              width: 100%;
            }

            .announcement-banner {
              align-items: flex-start;
              border-radius: 16px;
              padding: 10px 12px;
              gap: 10px;
            }

            .announcement-tag {
              margin-top: 1px;
              padding: 3px 10px;
            }

            .announcement-text {
              font-size: 0.875rem;
            }
          }

          .dark-mode .home-container,
          .dark-mode .search-section h1 {
            color: #ffffff;
          }

          .dark-mode .option-card {
            background-color: #222328;
            color: #d8d8d8;
            border: 1px solid #322f2f;
          }

          .dark-mode .option-card:hover {
            background-color: #1a191d;
            color: #ffffff;
          }

          .dark-mode .announcement-banner {
            background-color: #222328;
            border: 1px solid #322f2f;
          }

          .dark-mode .announcement-tag {
            background-color: #b66dff;
            color: #ffffff;
          }

          .dark-mode .announcement-text {
            color: #ffffff;
          }

          .dark-mode .search-button {
            background-color: #9a55ff;
            color: #ffffff;
          }

          .dark-mode .search-button:hover {
            background-color: #7b39db;
          }

          .dark-mode .home-container .MuiFormLabel-root {
            color: #9c9fa6;
          }

          .dark-mode .home-container .MuiFormLabel-root.Mui-focused {
            color: #b66dff;
          }

          .dark-mode .home-container .MuiOutlinedInput-root {
            background-color: #222328;
            color: #ffffff;
          }

          .dark-mode .home-container .MuiOutlinedInput-root fieldset {
            border-color: #322f2f;
          }

          .dark-mode .home-container .MuiOutlinedInput-root:hover fieldset {
            border-color: #9c9fa6;
          }

          .dark-mode .home-container .MuiOutlinedInput-root.Mui-focused fieldset {
            border-color: #b66dff;
          }

          .dark-mode .home-container .MuiAutocomplete-popupIndicator,
          .dark-mode .home-container .MuiAutocomplete-clearIndicator {
            color: #9c9fa6;
          }

          .dark-mode .home-container .MuiInputBase-input::placeholder {
            color: #c9c8c8;
            opacity: 1;
          }
        `}
      </style>
      <div className="home-container">
        <div className="announcement-container">
          <a href="/3x-more-free-ai-usage-lower-prices-same-contrarian-approach-to-stock-picking" className="announcement-link">
            <div className="announcement-banner">
              <span className="announcement-tag">New</span>
              <span className="announcement-text">
                3x More Free AI Usage, Lower Prices, Same Contrarian Approach to Stock Picking
                <span className="announcement-arrow">›</span>
              </span>
            </div>
          </a>
        </div>
        <div className="search-section">
          <h1>Search Decode Investing</h1>
          <form className="search-form" action="/search" method="get" onSubmit={(e) => {
            e.preventDefault();
            runSearch(inputValue);
          }}>
            <Autocomplete<string, false, false, true>
              value={value}
              onChange={(event: React.SyntheticEvent, newValue: string | null, reason) => {
                setValue(newValue);
                if (typeof newValue === 'string') {
                  setInputValue(newValue);
                  if (reason === 'selectOption') {
                    runSearch(newValue);
                  }
                }
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
                fetchAutoComplete(newInputValue);
              }}
              selectOnFocus
              clearOnBlur={false}
              handleHomeEndKeys
              id="stock-search"
              options={options}
              getOptionLabel={(option) => option}
              fullWidth
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="q"
                  label="Search stocks, pages, and questions"
                  variant="outlined"
                  sx={{ flexGrow: 1 }}
                  autoComplete="off"
                />
              )}
            />
            <IconButton type="submit" className="search-button" aria-label="search">
              <SearchIcon />
            </IconButton>
          </form>
        </div>
        <div className="options-grid">
          {optionsList.map((option) => (
            <a key={option.name} href={option.link} className="option-card">
              <div className="option-content">
                <option.icon className="option-icon" style={{ color: option.color }} />
                <span className="option-name">{option.name}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </>
  );
}
